import React, { useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Layout from './Layout';
import Login from './Login';
import Users from './Users';
import Admins from './Admins';
import NoTraffic from './NoTraffic';
import FailedLogins from './FailedLogins';
import RecentTraffic from './RecentTraffic';
import RecentFraud from './RecentFraud';
import RecentErrors from './RecentErrors';
import RecentLostRecords from './RecentLostRecords';
import Cloud from './Cloud';
import AutomationResults from './AutomationResults';
import ObfuscateCode from './ObfuscateCode';

function numDays (createdAt) {
  let date = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

function numMinutes (createdAt) {
  let date = new Date(createdAt);
  let now = new Date();
  let diffTime = Math.abs(now - new Date(date));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));

  return diffMinutes;
}

const Dashboard = () => {
  let [status, setStatus] = React.useState({
    'users': 'loading',
    'admins': 'loading',
    'no-traffic': 'loading',
    'failed-logins': 'loading',
    'recent-traffic': 'loading',
    'recent-fraud': 'loading',
    'recent-errors': 'loading',
    'recent-lost-records': 'loading',
    'cloud': 'loading',
    'automation-results': 'loading',
    'obfuscate-code': 'none',
  });

  let [recentTrafficEntries, setRecentTrafficEntries] = useState(null);
  let [recentFraudEntries, setRecentFraudEntries] = useState(null);
  let [users, setUsers] = useState(null);
  let [admins, setAdmins] = useState(null);
  let [noTrafficEntries, setNoTrafficEntries] = useState(null);
  let [failedLoginEntries, setFailedLoginEntries] = useState(null);
  let [recentErrorsEntries, setRecentErrorEntries] = useState(null);
  let [recentLostRecordEntries, setRecentLostRecordEntries] = useState(null);
  let [queue, setQueue] = React.useState(null);
  let [servers, setServers] = React.useState(null);
  let [automationResultsEntries, setAutomationResultsEntries] = useState(null);

  useEffect(() => {
    const api = async (path) => {
      const res = await fetch(process.env.REACT_APP_SERVER_URL + path, {
        method: 'POST',
        headers: {
          Authorization: localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Request failed');
      return json;
    };

    (async () => {
      try {
        let data, status, count;

        let limited = localStorage.getItem('limited') === 'true';

        if (!limited) {
          data = await api('/api/get-recent-traffic');
          setRecentTrafficEntries(data.entries);
          status = numMinutes(data.entries[0].created_at) >= 5 ? 'error' : 'ok';
          setStatus(prevStatus => ({...prevStatus, 'recent-traffic': status}));

          data = await api('/api/get-recent-fraud');
          setRecentFraudEntries(data.entries);
          status = numMinutes(data.entries[0].created_at) >= 5 ? 'error' : 'ok';
          setStatus(prevStatus => ({...prevStatus, 'recent-fraud': status}));

          data = await api('/api/get-all-users');
          setUsers(data.users);
          status = data.users.some(user => user.stripeCustomerId === null) ? 'error' : 'ok'; setStatus(prevStatus => ({...prevStatus, users: status}));
          setStatus(prevStatus => ({...prevStatus, users: status}));

          data = await api('/api/get-admins');
          setAdmins(data.admins);
          setStatus(prevStatus => ({...prevStatus, admins: 'none'}));

          data = await api('/api/get-no-traffic');
          setNoTrafficEntries(data.entries);
          status = data.entries.length > 0 ? 'error' : 'ok';
          setStatus(prevStatus => ({...prevStatus, 'no-traffic': status}));

          data = await api('/api/get-failed-logins');
          setFailedLoginEntries(data.entries);
          status = 'ok';
          for (let entry of data.entries) {
            if (numDays(entry.created_at) <= 7) {
              status = 'error';
              break;
            }
          }
          setStatus(prevStatus => ({...prevStatus, 'failed-logins': status}));

          data = await api('/api/get-recent-errors');
          setRecentErrorEntries(data.entries);
          count = 0;
          for (let entry of data.entries) {
            if (numMinutes(entry.created_at) <= 5) {
              count++;
            }
          }
          status = count >= 20 ? 'error' : 'ok';
          setStatus(prevStatus => ({...prevStatus, 'recent-errors': status}));

          data = await api('/api/get-recent-lost-records');
          setRecentLostRecordEntries(data.entries);
          count = 0;
          for (let entry of data.entries) {
            if (numMinutes(entry.created_at) <= 5) {
              count++;
            }
          }
          status = count >= 20 ? 'error' : 'ok';
          setStatus(prevStatus => ({...prevStatus, 'recent-lost-records': status}));
        }

        data = await api('/api/get-automation-results');
        setAutomationResultsEntries(data.entries);
        status = 'ok';
        if (data.entries.length !== 0) {
          status = 'error';
        }
        setStatus(prevStatus => ({...prevStatus, 'automation-results': status}));

        if (!limited) {
          data = await api('/api/get-cloud-status');
          setQueue(data.queue);
          setServers(data.servers);
          status = 'ok';
          if (data.queue > 20000) {
            status = 'error';
          }
          for (let server of data.servers) {
            if (!server.ping) {
              status = 'error';
              break;
            }

            if (server.http !== undefined && !server.http) {
              status = 'error';
              break;
            }
          }
          setStatus(prevStatus => ({...prevStatus, cloud: status}));
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
      }
    })();
  }, []);

  return (
    <Routes>
      <Route element={<Layout status={status} />}>
        <Route path="/users"
               element={<Users users={users}
                               setUsers={setUsers} />} />
        <Route path="/admins"
               element={<Admins admins={admins}
                                setAdmins={setAdmins} />} />
        <Route path="/no-traffic"
               element={<NoTraffic entries={noTrafficEntries}
                                   setEntries={setNoTrafficEntries} />} />
        <Route path="/failed-logins"
               element={<FailedLogins entries={failedLoginEntries} />} />
        <Route path="/recent-traffic"
               element={<RecentTraffic entries={recentTrafficEntries} />} />
        <Route path="/recent-fraud"
               element={<RecentFraud entries={recentFraudEntries} />} />
        <Route path="/recent-errors"
               element={<RecentErrors entries={recentErrorsEntries} />} />
        <Route path="/recent-lost-records"
               element={<RecentLostRecords entries={recentLostRecordEntries} />} />
        <Route path="/cloud"
               element={<Cloud queue={queue}
                               servers={servers} />} />
        <Route path="/automation-results"
               element={<AutomationResults entries={automationResultsEntries} />} />
        <Route path="/obfuscate-code"
               element={<ObfuscateCode />} />
        <Route path="*" element={<div style={{
                                        width: '100%',
                                        height: '100%',
                                        fontSize: 20 }}>
                                   Page not found
                                 </div>} />
      </Route>
    </Routes>
  );
};

export default Dashboard;
